import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { Navigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";

// Export API URL for use in components
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to set token in axios defaults
  const setAxiosToken = useCallback((token) => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, []);

  // Function to handle logout
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setAxiosToken(null);
    setUser(null);
  }, [setAxiosToken]);

  // Set up axios interceptor for 401 responses
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // If we get a 401, the token is invalid/expired - logout
        if (error.response?.status === 401) {
          console.warn("401 received - token expired or invalid");
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  // Listen for storage changes (from other tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "token") {
        if (e.newValue) {
          // Token was set in another tab
          setAxiosToken(e.newValue);
          const storedUser = localStorage.getItem("user");
          if (storedUser) {
            try {
              setUser(JSON.parse(storedUser));
            } catch (err) {
              console.error("Failed to parse stored user:", err);
            }
          }
        } else {
          // Token was removed in another tab - logout
          logout();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [logout, setAxiosToken]);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = () => {
      const token = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");
      
      if (token && storedUser) {
        try {
          // Set token in axios defaults IMMEDIATELY
          setAxiosToken(token);
          setUser(JSON.parse(storedUser));
        } catch (err) {
          console.error("Failed to restore auth state:", err);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [logout, setAxiosToken]);

  const login = async (email, password) => {
    const res = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password });
    const { token, user: userData } = res.data;
    
    // Store in localStorage
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    
    // Set axios defaults
    setAxiosToken(token);
    setUser(userData);
    
    return userData;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// ============================================================
// iter193 — Role-aware navigation helper.
//
// Returns the canonical "home" route for the currently logged-in
// user's role. Used by every back/exit button on cross-role
// pages (MOT workspace, VI sections, header logo, etc.) so a
// FOH/ADMIN user clicking Exit doesn't land on the technician
// dashboard, and a MOT tester doesn't get bumped to FOH.
//
// Role → dashboard:
//   MOT_TESTER          → /app/tech/mot
//   TECH / TECHNICIAN   → /app/tech/jobs
//   FOH / ADMIN / other → /app/foh
//
// Optional ``forJob(jobId)`` returns the role-aware "back to this
// job" target — FOH/ADMIN to /app/foh/job/:id, testers/tech to
// /app/tech/job/:id/sections.
// ============================================================
export const useRoleHome = () => {
  const { user } = useAuth();
  const role = String(user?.role || "").toUpperCase();
  if (role === "MOT_TESTER") return "/app/tech/mot";
  if (role === "TECH" || role === "TECHNICIAN") return "/app/tech/jobs";
  return "/app/foh";
};

export const useRoleAwareBackToJob = (jobId) => {
  const { user } = useAuth();
  const role = String(user?.role || "").toUpperCase();
  // iter196 — MOT_TESTER canonical home is /app/tech/mot (the MOT
  // queue). Sending them to /app/tech/job/:id/sections after exiting
  // an MOT workspace was sending them to a section-list page they
  // don't use. Bounce them to their dashboard instead so "Back to
  // jobs" actually returns to the jobs list they came from.
  if (role === "MOT_TESTER") return "/app/tech/mot";
  if (role === "TECH" || role === "TECHNICIAN") return `/app/tech/job/${jobId}/sections`;
  return `/app/foh/job/${jobId}`;
};

export const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.map((r) => String(r).toUpperCase()).includes(String(user.role || "").toUpperCase())) {
    // Send users to their natural landing page when they hit a route their role can't access
    const role = String(user.role || "").toUpperCase();
    if (role === "MOT_TESTER") return <Navigate to="/app/tech/mot" replace />;
    if (role === "TECH" || role === "TECHNICIAN") return <Navigate to="/app/tech" replace />;
    return <Navigate to="/app/foh" replace />;
  }

  return children;
};

// Redirect component for new intake flow
export const NewToIntakeRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700"></div>
      </div>
    );
  }

  // If user is logged in with FOH role, redirect to intake
  if (user && user.role === "foh") {
    return <Navigate to="/app/foh/intake" replace />;
  }

  // Otherwise redirect to login
  return <Navigate to="/login" replace />;
};

// All exports are already declared above
